import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Anchor, Button } from '@mantine/core';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { AlertCircle } from 'tabler-icons-react';
import TableView from '../../common/TableView';
import { ECOM_REPORT_ENUM, REPORT_CATEGORY_LIST } from './reportsConfig';
import { useReportHistory } from '../../../helpers/reportHelper';
import AdminView from '../../common/AdminView';
import AppCard from '../../common/AppCard';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import GenerateReportModal from '../reports/GenerateReportModal';
import { useModalState } from '../../../helpers/hooks';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';

const TABLE_COLUMNS = [
  {
    label: 'Title',
    value: 'title',
    sortable: true
  },
  {
    label: 'Type',
    value: 'type',
    sortable: true
  },
  {
    label: 'Last viewed',
    value: 'last_viewed',
    sortable: true
  }
];

const ReportTableListView = ({ reports, baseUrl, loading, ...rest }) => {
  const { reportHistory } = useReportHistory();
  const { state: dashboardState, changeReportViewSettings } = useContext(
    RegistrationAdminDashboardContext
  );
  const { state } = useContext(RegistrationAdminContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const [filterState, setFilterState] = useState({
    search: '',
    fkReportCategory: null
  });

  return (
    <AdminView
      customControl={
        <>
          <Button
            color="dark"
            onClick={() => {
              changeReportViewSettings({
                isClassicView: !dashboardState.view.isClassicReportView
              });
            }}
            variant="outline"
          >
            Legacy view
          </Button>

          <Button
            color="dark"
            component={Link}
            size="sm"
            to={`${baseUrl}/reports/${ECOM_REPORT_ENUM.FINANCE_SUMMARY}`}
          >
            View summary
          </Button>
        </>
      }
      infoContent={
        <>
          <AppStack>
            <AlertCircle color="dodgerblue" size={22} />
          </AppStack>
          <AppText style={{ fontSize: 14 }}>
            You're currently experiencing the new reports interface. To return
            to the classic view,{' '}
            <Anchor
              color="dodgerblue"
              onClick={(e) => {
                e.preventDefault();
                changeReportViewSettings({isClassicView: !dashboardState.view.isClassicReportView});
              }}
              style={{ fontSize: 14 }}
            >
              click here.
            </Anchor>
          </AppText>
        </>
      }
      {...rest}
    >
      <AppCard
        style={{ flex: 1, padding: 0, height: '100%', borderTop: 'none' }}
        withBorder
      >
        <TableView
          columns={TABLE_COLUMNS}
          defaultSort="title"
          emptyMessage="No reports found."
          filters={[
            {
              key: 1,
              label: 'Search',
              placeholder: 'Filter by title',
              value: filterState.search,
              onChange: (value) =>
                setFilterState({ ...filterState, search: value })
            },
            {
              key: 2,
              label: 'Category',
              type: 'select',
              placeholder: 'Filter by type',
              value: filterState.fkReportCategory,
              searchable: true,
              clearable: true,
              data: REPORT_CATEGORY_LIST,
              onChange: (value) =>
                setFilterState({ ...filterState, fkReportCategory: value })
            }
          ]}
          isLoading={loading}
          minRowHeight="unset"
          rows={reports
            .filter(
              (r) =>
                !r.isHidden &&
                (!filterState.fkReportCategory ||
                  r.fkReportCategory === filterState.fkReportCategory) &&
                (!filterState.search ||
                  r.title
                    .toLowerCase()
                    .includes(filterState.search?.toLowerCase()) ||
                  r.description
                    ?.toLowerCase()
                    .includes(filterState.search?.toLowerCase()))
            )
            .map((a) => {
              const viewHistory = reportHistory.reports.find(
                (f) => f.uuid === a.uuid
              );
              const reportTemplate = a.reportTemplateUuid
                ? state.regReports.value.find(
                    (f) => f.guid === a.reportTemplateUuid
                  )
                : null;

              return {
                key: a.uuid,
                columns: [
                  {
                    key: 1,
                    label: a.title,
                    weight: 500,
                    subLabel: a.description,
                    ...(reportTemplate
                      ? {
                          onClick: () =>
                            onOpenModal(
                              VIEW_ACTIONS_ENUM.generate,
                              reportTemplate
                            )
                        }
                      : {
                          component: Link,
                          to: `${baseUrl}/reports/${a.uuid}`
                        })
                  },
                  {
                    key: 2,
                    label: REPORT_CATEGORY_LIST.find(
                      (f) =>
                        f.value.toString() === a.fkReportCategory.toString()
                    ).label
                  },
                  {
                    key: 3,
                    label: viewHistory
                      ? dayjs(viewHistory.date).format('MMM DD, YYYY')
                      : '',
                    sortValue: viewHistory ? new Date(viewHistory.date) : null,
                    sortValueType: 'date'
                  }
                ]
              };
            })}
          tableTitle="Reports"
        />
      </AppCard>

      <GenerateReportModal
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.generate
        }
        onClose={onCloseModal}
        pkRegAssociation={dashboardState.regAssociation.value?.pkRegAssociation}
        regReportTemplate={modalState.item}
      />
    </AdminView>
  );
};

ReportTableListView.propTypes = {
  baseUrl: PropTypes.string,
  loading: PropTypes.bool,
  reports: PropTypes.array
};

export default ReportTableListView;
